/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MASQUERADER_LIST } from "../graphql/getUserAdvancedSearch.query";
import { GET_USER_WITH_BOXES } from "../graphql/getUsersWithBoxes.query";
import config from "../config/userLists.config.json";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";
import { GET_PRESENTATIONS } from "../graphql/getPresentation.query";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IMasqueraderListsMethods extends IPluginMethodMap {
  getMasqueraderListsProps(args: any): Promise<any>;
}

export class MasqueraderListsService implements IAgentPlugin {
  readonly methods: IMasqueraderListsMethods;

  constructor() {
    this.methods = {
      getMasqueraderListsProps: this.getMasqueraderListsProps.bind(this),
    };
  }

  async getMasqueraderListsProps(args: any) {
    console.log("getMasqueraderListsProps--->", args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    let presentationId = args?.criteria?.presentationId;

    // if (!presentationId || !args?.criteria?.lateAssociation) {
    //   return {
    //     isEmpty: true
    //   };
    // }

    if (!presentationId) {
      const _filter = {
        status: {
          eq: "active",
        },
      };
      const presentation = await client.query({
        query: GET_PRESENTATIONS,
        variables: {
          filter: _filter,
          options: {
            limit: args?.size ?? 10,
            skip: args?.offset ?? 0,
            sort: {
              field: "status",
              order: "asc",
            },
          },
        },
        fetchPolicy: "network-only",
      });
      if (presentation?.data?.getPresentations?.content[0].id) {
        presentationId = presentation?.data?.getPresentations?.content[0].id;
      } else {
        throw Error("Presentation not found");
      }
    }
    if (
      args.criteria.lateAssociation === "Boxes___discount___lateAssociation" ||
      !args?.criteria?.lateAssociation
    ) {
      filter = {
        ...filter,
        Boxes___discount___lateAssociation: {
          eq: true,
        },
      };
    }
    if (args.criteria.lateAssociation === "Referal___inviteType") {
      filter = {
        ...filter,
        Referal___inviteType: {
          eq: "Discount",
        },
      };
    }
    // this.getMultiFilter(args?.criteria);

    // const searchType = this.determineSearchType(args?.criteria);
    // if ( searchType && searchType !== null ) {
    //   if ( searchType === "multi"){
    //     filter = this.getMultiFilter(args?.criteria);
    //   }else{
    //     filter = this.convertCriteria(args?.criteria);
    //   }
    // }
    const options = {
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
      sort: {
        field: "createdAt",
        order: "desc",
      },
    };
    try {
      if (client) {
        const res = await client.query({
          query: GET_USER_WITH_BOXES,
          variables: {
            options: options,
            presentationId: presentationId,
            filter: filter,
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _rowData = this.formatUserTable(
            res?.data?.getUsersWithBoxes?.content
          );
          let _totalPages = res?.data?.getUsersWithBoxes?.count
            ? Math.ceil(parseInt(res?.data?.getUsersWithBoxes?.count) / 10)
            : 1;

          let _currentPage =
            res?.data?.getUsersWithBoxes?.skip /
              res?.data?.getUsersWithBoxes?.limit +
            1;
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getUsersWithBoxes?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: res?.data?.getUsersWithBoxes?.limit,
            totalCount: res?.data?.getUsersWithBoxes?.count,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatUserTable(data: any) {
    console.log("formatUserTable--->", data);
    let _rowData: any = [];
    data.forEach((item: any) => {
      _rowData.push({
        id: item?.id,
        name: item?.fullName ?? "Null Null",
        email: item?.email ?? "null",
        gender: item?.gender ?? "-",
        sponseredTo: item?.Boxes[0]?.discount?.sponserName ?? "-",
        costumeId: item?.Boxes[0]?.costumeID ?? "-",
        inviteType: item?.Referal?.inviteType ?? "-",
        partition: item?.Referal?.partition ?? "-",
        totalAmount: item?.Boxes[0]?.totalAmount ?? "-",
        invitedBy: item?.creatorUser?.fullName ?? "-",
        section: item?.Boxes[0]?.section?.name ?? "-",
        costumeTypes: item?.Boxes[0]?.items[0]?.costumeType?.name ?? "-",
        paymentStats: item?.Boxes[0]?.paymentStats ?? "-",
        allDetails: item,
      });
    });
    return _rowData;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      result[key] = { contains: criteria[key] };
    }
    return result;
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        transformedCriteria[inputData.id] = {
          [filterType.toLowerCase().replace("equal", "eq")]:
            filters[filterType],
        };
      }
    }
    return transformedCriteria;
  }
}
